import React, { useEffect, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Bell, ChevronDown, Menu, X } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageToggle from "@/components/LanguageToggle";
import SupportButton from "@/components/SupportButton";
import EnergyBalanceIndicator from "@/components/EnergyBalanceIndicator";
import SessionActivityTracker from "@/components/SessionActivityTracker";
import { KD9LogoCircle, KD9LogoHorizontal } from "@/components/KD9Logo";
import { useRole, ROLE_META } from "@/context/RoleContext";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

/* KD9 · compose topbar role label from canonical roles + Academy flags. */
function composeRoleDisplay(user) {
  const roles = user?.roles || [];
  const flags = user?.flags || {};
  if (roles.includes("director")) return "Director KD9";
  if (roles.includes("admin"))    return "Administrador KD9";
  const parts = ["Usuario"];
  if (roles.includes("support"))    parts.push("Soporte");
  if (roles.includes("consultant")) parts.push("Consultor");
  if (roles.includes("creator"))    parts.push("Creador");
  const isStudent = roles.includes("student") || flags.student_active;
  const academyPremium = flags.academy_premium;
  if (isStudent) parts.push(academyPremium ? "Estudiante Premium" : "Estudiante");
  else if (academyPremium) parts.push("Estudiante Premium");
  return parts.join(" • ");
}


function labelResponsiveTables(root) {
  root?.querySelectorAll("table").forEach((table) => {
    const labels = Array.from(table.querySelectorAll("thead th")).map((cell) =>
      cell.textContent.trim()
    );
    table.querySelectorAll("tbody tr").forEach((row) => {
      Array.from(row.children).forEach((cell, index) => {
        if (cell.tagName === "TD" && labels[index] && !cell.dataset.label) {
          cell.dataset.label = labels[index];
        }
      });
    });
  });
}

const DashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { role } = useRole();
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const meta = ROLE_META[role] || ROLE_META.user;
  const fullName = [user?.name, user?.surname].filter(Boolean).join(" ").trim();
  const displayName = (fullName || user?.name || "KD9").split(" ")[0] || "KD9";
  const roleLabel = composeRoleDisplay(user);


  // Presentation-only enhancement: derive mobile card labels from each table
  // header, including rows loaded later from the API.
  useEffect(() => {
    const root = document.querySelector(".kd9-responsive-content");
    if (!root) return undefined;
    labelResponsiveTables(root);
    const observer = new MutationObserver(() => labelResponsiveTables(root));
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Close mobile drawer when route changes (e.g. user taps a sidebar link).
  useEffect(() => {
    const id = setTimeout(() => setMobileOpen(false), 0);
    return () => clearTimeout(id);
  }, [location.pathname]);

  // Prevent the dashboard behind the mobile drawer from scrolling.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [mobileOpen]);

  return (
    <div className="flex min-h-screen" style={{ background: "var(--kd9-bg)" }}>
      <SessionActivityTracker />
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      {/* mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div
            id="kd9-mobile-navigation"
            className="absolute left-0 top-0 bottom-0 w-[min(18rem,calc(100vw-2rem))]"
            data-testid="mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navegación KD9"
            onClick={(e) => {
              // Close drawer when user taps any link or the logout button inside it.
              const tgt = e.target.closest('a, button[data-testid="sidebar-logout-btn"]');
              if (tgt) setMobileOpen(false);
            }}
          >
            <Sidebar collapsed={false} setCollapsed={() => setMobileOpen(false)} forceVisible />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* topbar */}
        <header
          className="sticky top-0 z-40 backdrop-blur-xl border-b"
          style={{
            background: "var(--kd9-overlay)",
            borderColor: "var(--kd9-border)",
          }}
          data-testid="dashboard-topbar"
        >
          <div className="px-3 sm:px-6 lg:px-10 min-h-16 sm:h-20 flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-2 lg:hidden min-w-0">
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-expanded={mobileOpen}
                aria-controls="kd9-mobile-navigation"
                className="w-10 h-10 rounded-full flex items-center justify-center border"
                style={{ borderColor: "var(--kd9-border)" }}
                data-testid="topbar-menu-btn"
                aria-label="Abrir menú"
              >
                {mobileOpen ? (
                  <X size={20} style={{ color: "var(--kd9-gold)" }} />
                ) : (
                  <Menu size={20} style={{ color: "var(--kd9-gold)" }} />
                )}
              </button>
              <KD9LogoHorizontal size={32} />
            </div>

            <div className="flex-1" aria-hidden="true" />

            <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
              {/* Read-only role badge — driven by /api/me/access-map */}
              <div
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border"
                style={{
                  borderColor: `${meta.color}55`,
                  background: `${meta.color}14`,
                  color: meta.color,
                }}
                data-testid="topbar-role-badge"
                aria-label={`Rol: ${roleLabel}`}
                title={`Rol: ${roleLabel}`}
              >
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center font-cinzel text-[10px]"
                  style={{ background: meta.color, color: "#0a0a0a" }}
                >
                  {meta.glyph}
                </span>
                <span className="font-mont text-[10px] tracking-[0.22em] uppercase font-semibold">
                  {roleLabel}
                </span>
              </div>
              <EnergyBalanceIndicator />
              <Link
                to="/app/mensajes"
                className="w-10 h-10 rounded-full flex items-center justify-center border"
                style={{ borderColor: "var(--kd9-border)" }}
                aria-label="Centro de Comunicaciones KD9"
                data-testid="topbar-communications-link"
              >
                <Bell size={17} style={{ color: "var(--kd9-accent)" }} />
              </Link>
              <LanguageToggle />
              <ThemeToggle />

              <Link
                to="/app/perfil"
                className="flex shrink-0 items-center gap-1.5 sm:gap-3 p-1 sm:pl-2 sm:pr-3 sm:py-1.5 rounded-full border transition-all hover:-translate-y-0.5"
                style={{ borderColor: "var(--kd9-border)" }}
                data-testid="topbar-profile-link"
              >
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover"
                    style={{ border: "1px solid var(--kd9-gold)" }}
                    data-testid="topbar-avatar-img"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <KD9LogoCircle size={36} glow={false} />
                )}
                <div className="hidden md:block leading-tight">
                  <div className="font-mont text-sm font-semibold" data-testid="topbar-username">
                    {t("topbar.greeting").replace("{name}", displayName)}
                  </div>
                  <div
                    className="font-mont text-[11px]"
                    style={{ color: "var(--kd9-text-soft)" }}
                    data-testid="topbar-role-line"
                  >
                    {roleLabel}
                  </div>
                </div>
                <ChevronDown size={14} style={{ color: "var(--kd9-gold)" }} />
              </Link>
            </div>
          </div>
        </header>

        <div className="kd9-responsive-content flex-1 p-4 sm:p-6 lg:p-10 relative overflow-hidden">
          <Outlet />
        </div>
        {role !== "superadmin" && <SupportButton />}
      </main>
    </div>
  );
};

export default DashboardLayout;
